import {
   Button,
   Box,
   Typography,
   Grid,
   AppBar,
   Toolbar,
   Link,
   List,
   ListItem,
   Paper,
} from '@mui/material';
import {
   Tv as TvIcon,
   QrCode2 as QrCodeIcon,
   Image as ImageIcon,
   Star as StarIcon,
   Check as CheckIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/images/logo.png';
import {
   stripPhotos,
   screenDemoPhotos,
   phoneGridPhotos,
   phoneAvatarPhoto,
} from '../assets/landing/landingPhotos';

const BG = '#080A0F';
const BG_S = '#0E1118';
const BG_E = '#141820';
const TEAL = '#00C4A7';
const AMBER = '#F0A500';
const MUTED = 'rgba(255,255,255,0.45)';
const FONT_SERIF = '"Cormorant Garamond", serif';
const FONT_SANS = '"DM Sans", sans-serif';
const FONT_MONO = '"DM Mono", monospace';

export default function Home() {
   const navigate = useNavigate();

   const scrollToSection = (sectionId: string) => {
      const element = document.getElementById(sectionId);
      if (element) {
         element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
   };

   return (
      <Box sx={{ bgcolor: BG, color: 'white', minHeight: '100vh', overflowX: 'hidden' }}>
         {/* Navigation */}
         <AppBar
            position='fixed'
            elevation={0}
            sx={{
               bgcolor: 'rgba(8,10,15,0.85)',
               backdropFilter: 'blur(20px)',
               borderBottom: '1px solid rgba(255,255,255,0.06)',
               borderRadius: 0,
            }}
            data-testid='navigation'
         >
            <Toolbar sx={{ height: 64, px: { xs: 2, md: 4 }, justifyContent: 'space-between', borderRadius: 0 }}>
               <Box
                  component='a'
                  href='/'
                  onClick={(e: React.MouseEvent) => {
                     e.preventDefault();
                     navigate('/');
                  }}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.25, textDecoration: 'none' }}
               >
                  <Box
                     sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        flexShrink: 0,
                        cursor: 'pointer',
                     }}
                     data-testid='logo'
                  >
                     <Box
                        component='img'
                        src={logo}
                        alt='WeTrippen'
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                     />
                  </Box>
                  <Typography
                     sx={{
                        fontFamily: FONT_SERIF,
                        fontSize: 22,
                        fontWeight: 600,
                        color: TEAL,
                        letterSpacing: -0.3,
                     }}
                  >
                     WeTrippen
                  </Typography>
               </Box>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Button
                     onClick={() => navigate('/login')}
                     sx={{
                        fontFamily: FONT_SANS,
                        fontSize: 13,
                        fontWeight: 500,
                        color: 'rgba(255,255,255,0.5)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: 1,
                        px: 2.5,
                        py: 1.125,
                        '&:hover': { color: 'white', borderColor: 'rgba(255,255,255,0.3)' },
                     }}
                  >
                     Sign In
                  </Button>
                  <Button
                     onClick={() => navigate('/signup')}
                     sx={{
                        fontFamily: FONT_SANS,
                        fontSize: 13,
                        fontWeight: 700,
                        bgcolor: TEAL,
                        color: '#000',
                        borderRadius: 1,
                        px: 2.75,
                        py: 1.125,
                        '&:hover': {
                           bgcolor: '#00dfc0',
                           transform: 'translateY(-1px)',
                           boxShadow: '0 6px 20px rgba(0,196,167,0.3)',
                        },
                     }}
                     data-testid='nav-cta-signup'
                  >
                     Join Free
                  </Button>
               </Box>
            </Toolbar>
         </AppBar>

         {/* Hero */}
         <Box
            data-testid='hero-section'
            sx={{
               minHeight: '100vh',
               display: 'flex',
               flexDirection: 'column',
               alignItems: 'center',
               justifyContent: 'center',
               textAlign: 'center',
               pt: 15,
               pb: 10,
               px: 3,
               position: 'relative',
               overflow: 'hidden',
            }}
         >
            <Box
               sx={{
                  position: 'absolute',
                  top: '-20%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 900,
                  height: 600,
                  borderRadius: '50%',
                  background: 'radial-gradient(ellipse, rgba(0,196,167,0.12) 0%, transparent 65%)',
                  pointerEvents: 'none',
               }}
            />
            <Box
               sx={{
                  position: 'absolute',
                  inset: 0,
                  backgroundImage: `linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)`,
                  backgroundSize: '64px 64px',
                  maskImage:
                     'radial-gradient(ellipse 70% 70% at 50% 40%, black 10%, transparent 80%)',
                  pointerEvents: 'none',
               }}
            />
            <Box sx={{ position: 'relative', zIndex: 2 }}>
               <Box
                  sx={{
                     display: 'inline-flex',
                     alignItems: 'center',
                     gap: 1,
                     bgcolor: 'rgba(0,196,167,0.08)',
                     border: '1px solid rgba(0,196,167,0.2)',
                     borderRadius: 999,
                     py: 0.875,
                     px: 2.25,
                     mb: 4,
                     fontFamily: FONT_MONO,
                     fontSize: 9,
                     letterSpacing: 2.5,
                     textTransform: 'uppercase',
                     color: TEAL,
                  }}
               >
                  <Box
                     sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: TEAL,
                        flexShrink: 0,
                        animation: 'pulse 2s ease-in-out infinite',
                     }}
                  />
                  First event free — then $35/year
               </Box>
               <Typography
                  component='h1'
                  sx={{
                     fontFamily: FONT_SERIF,
                     fontSize: { xs: 72, sm: 120, md: 148 },
                     fontWeight: 300,
                     lineHeight: 0.88,
                     letterSpacing: -4,
                     color: 'white',
                     mb: 1.5,
                  }}
                  data-testid='hero-headline'
               >
                  <Box
                     component='em'
                     sx={{ fontStyle: 'italic', fontWeight: 300, color: 'rgba(255,255,255,0.55)' }}
                  >
                     Your photos
                  </Box>
                  <br />
                  <Box
                     component='strong'
                     sx={{
                        fontWeight: 700,
                        fontStyle: 'normal',
                        color: 'white',
                        position: 'relative',
                        display: 'inline-block',
                        '&::after': {
                           content: '""',
                           position: 'absolute',
                           bottom: 6,
                           left: 0,
                           right: 0,
                           height: 3,
                           borderRadius: 2,
                           bgcolor: TEAL,
                        },
                     }}
                  >
                     found you.
                  </Box>
               </Typography>
               <Typography
                  sx={{
                     fontFamily: FONT_SANS,
                     fontSize: 18,
                     fontWeight: 300,
                     color: MUTED,
                     lineHeight: 1.8,
                     maxWidth: 440,
                     mx: 'auto',
                     mt: 3,
                     mb: 5,
                  }}
                  data-testid='hero-subheadline'
               >
                  Scan a QR at any event. Every photo you're in — saved to your feed instantly. No
                  chasing photographers. No losing memories.
               </Typography>
               <Box
                  sx={{
                     display: 'flex',
                     gap: 1.5,
                     justifyContent: 'center',
                     flexWrap: 'wrap',
                     mb: 2.5,
                  }}
               >
                  <Button
                     onClick={() => navigate('/signup')}
                     sx={{
                        fontFamily: FONT_SANS,
                        fontSize: 15,
                        fontWeight: 700,
                        bgcolor: TEAL,
                        color: '#000',
                        borderRadius: 2,
                        py: 2,
                        px: 4.5,
                        boxShadow: '0 4px 24px rgba(0,196,167,0.3)',
                        '&:hover': {
                           bgcolor: '#00dfc0',
                           transform: 'translateY(-2px)',
                           boxShadow: '0 8px 32px rgba(0,196,167,0.4)',
                        },
                     }}
                     data-testid='hero-cta-primary'
                  >
                     Claim Your Photos
                  </Button>
                  <Button
                     onClick={() => scrollToSection('how')}
                     sx={{
                        fontFamily: FONT_SANS,
                        fontSize: 15,
                        fontWeight: 500,
                        color: 'rgba(255,255,255,0.6)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: 2,
                        py: 2,
                        px: 4.5,
                        '&:hover': {
                           color: 'white',
                           borderColor: 'rgba(255,255,255,0.35)',
                           transform: 'translateY(-1px)',
                        },
                     }}
                     data-testid='hero-cta-secondary'
                  >
                     See How It Works →
                  </Button>
               </Box>
               <Typography
                  sx={{
                     fontFamily: FONT_MONO,
                     fontSize: 9,
                     letterSpacing: 2,
                     textTransform: 'uppercase',
                     color: 'rgba(255,255,255,0.2)',
                  }}
                  data-testid='hero-trust-signal'
               >
                  First event free · $35/year after · No credit card to start
               </Typography>
            </Box>
         </Box>

         {/* Photo strip */}
         <Box
            sx={{
               position: 'relative',
               overflow: 'hidden',
               height: 180,
               borderTop: '1px solid rgba(255,255,255,0.05)',
               borderBottom: '1px solid rgba(255,255,255,0.05)',
               bgcolor: BG_S,
            }}
         >
            <Box
               sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  width: 120,
                  background: `linear-gradient(to right, ${BG}, transparent)`,
                  zIndex: 2,
               }}
            />
            <Box
               sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  right: 0,
                  width: 120,
                  background: `linear-gradient(to left, ${BG}, transparent)`,
                  zIndex: 2,
               }}
            />
            <Box
               sx={{
                  display: 'flex',
                  gap: 0.75,
                  alignItems: 'center',
                  height: '100%',
                  width: 'max-content',
                  px: 0.75,
                  animation: 'landingStripScroll 30s linear infinite',
               }}
            >
               {[...stripPhotos, ...stripPhotos].map((src, i) => (
                  <Box
                     key={i}
                     component='img'
                     src={src}
                     alt='Event or portrait'
                     loading='lazy'
                     onError={(e) => {
                        e.currentTarget.style.display = 'none';
                     }}
                     sx={{
                        width: 164,
                        height: 164,
                        borderRadius: 1.25,
                        flexShrink: 0,
                        objectFit: 'cover',
                        border: '1px solid rgba(255,255,255,0.06)',
                     }}
                  />
               ))}
            </Box>
         </Box>

         {/* How it works */}
         <Box
            id='how'
            sx={{ maxWidth: 1080, mx: 'auto', py: 12, px: 4 }}
            data-testid='how-it-works-section'
         >
            <Typography
               component='span'
               sx={{
                  display: 'block',
                  fontFamily: FONT_MONO,
                  fontSize: 9,
                  letterSpacing: 2.5,
                  textTransform: 'uppercase',
                  color: TEAL,
                  mb: 2,
               }}
            >
               For Models & Attendees
            </Typography>
            <Typography
               sx={{
                  fontFamily: FONT_SERIF,
                  fontSize: { xs: 38, md: 64 },
                  fontWeight: 300,
                  lineHeight: 1,
                  letterSpacing: -2,
                  color: 'white',
                  mb: 2,
               }}
               data-testid='how-it-works-title'
            >
               One scan.
               <br />
               <Box component='em' sx={{ color: TEAL }}>
                  Every photo.
               </Box>
            </Typography>
            <Typography
               sx={{
                  fontSize: 16,
                  fontWeight: 300,
                  color: MUTED,
                  lineHeight: 1.8,
                  maxWidth: 460,
                  mb: 7,
               }}
            >
               Every photographer using Sequel Studio delivers photos through WeTrippen. You're
               always one scan away.
            </Typography>
            <Grid
               container
               sx={{
                  mt: 7,
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: 2.25,
                  overflow: 'hidden',
               }}
            >
               {[
                  {
                     num: '01',
                     title: 'See the screen',
                     desc: "A live display runs at the event. Photos appear on the TV screen in real time as they're shot.",
                     Icon: TvIcon,
                  },
                  {
                     num: '02',
                     title: 'Scan the QR',
                     desc: 'Every screen shows a QR code. Scan it with your phone — no app download needed to start.',
                     Icon: QrCodeIcon,
                  },
                  {
                     num: '03',
                     title: 'Find your photos',
                     desc: 'Land on the album. All shots from this event are right there. No searching, no waiting.',
                     Icon: ImageIcon,
                  },
                  {
                     num: '04',
                     title: 'Save & follow',
                     desc: 'Save to your WeTrippen feed. Follow the photographer. Get notified every time new shots drop.',
                     Icon: StarIcon,
                  },
               ].map((step, idx) => {
                  const StepIcon = step.Icon;
                  return (
                  <Grid
                     item
                     xs={12}
                     sm={6}
                     md={3}
                     key={step.num}
                     sx={{
                        bgcolor: BG_S,
                        p: 4.5,
                        borderRight: idx < 3 ? '1px solid rgba(255,255,255,0.07)' : 'none',
                        '&:hover': { bgcolor: BG_E },
                     }}
                  >
                     <Typography
                        sx={{
                           fontFamily: FONT_MONO,
                           fontSize: 9,
                           letterSpacing: 2,
                           textTransform: 'uppercase',
                           color: 'rgba(255,255,255,0.2)',
                           mb: 2.5,
                        }}
                     >
                        {step.num}
                     </Typography>
                     <Box
                        sx={{
                           width: 48,
                           height: 48,
                           borderRadius: 1.5,
                           mb: 2.5,
                           border: '1px solid rgba(0,196,167,0.15)',
                           bgcolor: 'rgba(0,196,167,0.08)',
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           color: TEAL,
                        }}
                     >
                        <StepIcon sx={{ fontSize: 22 }} />
                     </Box>
                     <Typography
                        sx={{
                           fontFamily: FONT_SERIF,
                           fontSize: 22,
                           fontWeight: 600,
                           color: 'white',
                           letterSpacing: -0.3,
                           mb: 1.25,
                        }}
                     >
                        {step.title}
                     </Typography>
                     <Typography
                        sx={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', lineHeight: 1.7 }}
                     >
                        {step.desc}
                     </Typography>
                  </Grid>
               );})}
            </Grid>
         </Box>

         {/* Sequel strip */}
         <Box
            sx={{
               bgcolor: BG_S,
               borderTop: '1px solid rgba(255,255,255,0.05)',
               borderBottom: '1px solid rgba(255,255,255,0.05)',
               py: 2.5,
               px: 4,
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               gap: 2,
               flexWrap: 'wrap',
            }}
         >
            <Typography
               sx={{
                  fontFamily: FONT_MONO,
                  fontSize: 9,
                  letterSpacing: 2,
                  textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.25)',
               }}
            >
               Powered by
            </Typography>
            <Typography
               sx={{
                  fontFamily: FONT_SERIF,
                  fontSize: 18,
                  fontWeight: 600,
                  fontStyle: 'italic',
                  color: 'rgba(255,255,255,0.4)',
               }}
            >
               Sequel Studio
            </Typography>
            <Box sx={{ width: 1, height: 18, bgcolor: 'rgba(255,255,255,0.1)' }} />
            <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.25)' }}>
               The professional tool photographers use to tether, display, and deliver photos at
               live events
            </Typography>
            <Link
               href='https://studio.wetrippen.com'
               target='_blank'
               rel='noopener noreferrer'
               sx={{
                  fontFamily: FONT_MONO,
                  fontSize: 9,
                  letterSpacing: 1.5,
                  textTransform: 'uppercase',
                  color: TEAL,
                  textDecoration: 'none',
                  '&:hover': { color: '#00dfc0' },
               }}
            >
               Learn more →
            </Link>
         </Box>

         {/* Scan Demo + Feed section */}
         <Box sx={{ maxWidth: 1080, mx: 'auto', py: 12, px: 4 }}>
            <Grid container spacing={8} alignItems='center' sx={{ mb: 9 }}>
               <Grid item xs={12} md={6}>
                  <Typography
                     sx={{
                        fontFamily: FONT_MONO,
                        fontSize: 9,
                        letterSpacing: 2.5,
                        textTransform: 'uppercase',
                        color: TEAL,
                        mb: 2,
                     }}
                  >
                     Live at events
                  </Typography>
                  <Typography
                     sx={{
                        fontFamily: FONT_SERIF,
                        fontSize: { xs: 32, md: 52 },
                        fontWeight: 300,
                        lineHeight: 1.1,
                        letterSpacing: -0.5,
                        color: 'white',
                        mb: 2,
                     }}
                  >
                     The screen
                     <br />
                     tells you
                     <br />
                     <Box component='em' sx={{ color: TEAL }}>
                        when to scan.
                     </Box>
                  </Typography>
                  <Typography sx={{ fontSize: 16, fontWeight: 300, color: MUTED, lineHeight: 1.8 }}>
                     Photographers running Sequel Studio stream photos to a TV in real time. The QR
                     code on that screen goes straight to your album. No one needs to hand you
                     anything.
                  </Typography>
               </Grid>
               <Grid item xs={12} md={6}>
                  <Paper
                     elevation={0}
                     sx={{
                        bgcolor: BG_S,
                        border: '1px solid rgba(255,255,255,0.07)',
                        borderRadius: 2.5,
                        overflow: 'hidden',
                     }}
                  >
                     <Grid container>
                        <Grid
                           item
                           xs={12}
                           md={6}
                           sx={{ position: 'relative', bgcolor: '#000', aspectRatio: '16/9' }}
                        >
                           <Grid container sx={{ position: 'absolute', inset: 0 }}>
                              {screenDemoPhotos.map((src, i) => (
                                 <Grid item xs={4} key={i} sx={{ p: 0.25 }}>
                                    <Box
                                       component='img'
                                       src={src}
                                       alt='Event photo'
                                       loading='lazy'
                                       onError={(e) => {
                                          e.currentTarget.style.display = 'none';
                                       }}
                                       sx={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover',
                                          display: 'block',
                                       }}
                                    />
                                 </Grid>
                              ))}
                           </Grid>
                           <Box
                              sx={{
                                 position: 'absolute',
                                 inset: 0,
                                 background:
                                    'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 60%)',
                              }}
                           />
                           <Box
                              sx={{
                                 position: 'absolute',
                                 top: 10,
                                 left: 10,
                                 display: 'flex',
                                 alignItems: 'center',
                                 gap: 0.75,
                                 bgcolor: 'rgba(0,0,0,0.6)',
                                 backdropFilter: 'blur(8px)',
                                 border: '1px solid rgba(240,165,0,0.3)',
                                 borderRadius: 999,
                                 py: 0.5,
                                 px: 1.25,
                              }}
                           >
                              <Box
                                 sx={{
                                    width: 5,
                                    height: 5,
                                    borderRadius: '50%',
                                    bgcolor: AMBER,
                                    flexShrink: 0,
                                    animation: 'pulse 1.5s ease-in-out infinite',
                                 }}
                              />
                              <Typography
                                 sx={{
                                    fontFamily: FONT_MONO,
                                    fontSize: 7,
                                    letterSpacing: 2,
                                    textTransform: 'uppercase',
                                    color: AMBER,
                                 }}
                              >
                                 Live
                              </Typography>
                           </Box>
                           <Box
                              sx={{
                                 position: 'absolute',
                                 bottom: 10,
                                 right: 10,
                                 bgcolor: 'white',
                                 borderRadius: 1,
                                 p: 0.625,
                                 display: 'flex',
                                 flexDirection: 'column',
                                 alignItems: 'center',
                                 gap: 0.25,
                              }}
                           >
                              <Box
                                 component='svg'
                                 width={40}
                                 height={40}
                                 viewBox='0 0 14 14'
                                 sx={{ display: 'block' }}
                              >
                                 <rect width={14} height={14} fill='white' />
                                 <rect
                                    x={1}
                                    y={1}
                                    width={4}
                                    height={4}
                                    fill='none'
                                    stroke='black'
                                    strokeWidth={0.7}
                                 />
                                 <rect x={2} y={2} width={2} height={2} fill='black' />
                                 <rect
                                    x={9}
                                    y={1}
                                    width={4}
                                    height={4}
                                    fill='none'
                                    stroke='black'
                                    strokeWidth={0.7}
                                 />
                                 <rect x={10} y={2} width={2} height={2} fill='black' />
                                 <rect
                                    x={1}
                                    y={9}
                                    width={4}
                                    height={4}
                                    fill='none'
                                    stroke='black'
                                    strokeWidth={0.7}
                                 />
                                 <rect x={2} y={10} width={2} height={2} fill='black' />
                                 <rect x={6} y={6} width={2} height={2} fill='black' />
                              </Box>
                              <Typography
                                 sx={{
                                    fontFamily: FONT_MONO,
                                    fontSize: 5,
                                    letterSpacing: 1,
                                    textTransform: 'uppercase',
                                    color: 'rgba(0,0,0,0.5)',
                                 }}
                              >
                                 Scan to save
                              </Typography>
                           </Box>
                        </Grid>
                        <Grid
                           item
                           xs={12}
                           md={6}
                           sx={{
                              p: 3.5,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              gap: 2,
                           }}
                        >
                           <Typography
                              sx={{
                                 fontFamily: FONT_SERIF,
                                 fontSize: 22,
                                 fontWeight: 600,
                                 color: 'white',
                                 letterSpacing: -0.3,
                              }}
                           >
                              How to get your photos
                           </Typography>
                           <Typography sx={{ fontSize: 13, color: MUTED, lineHeight: 1.7 }}>
                              At any event with a live display screen, your photos are already
                              waiting.
                           </Typography>
                           {[1, 2, 3].map((n) => (
                              <Box
                                 key={n}
                                 sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}
                              >
                                 <Box
                                    sx={{
                                       width: 24,
                                       height: 24,
                                       borderRadius: '50%',
                                       bgcolor: 'rgba(0,196,167,0.1)',
                                       border: '1px solid rgba(0,196,167,0.25)',
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       fontFamily: FONT_MONO,
                                       fontSize: 9,
                                       color: TEAL,
                                    }}
                                 >
                                    {n}
                                 </Box>
                                 <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
                                    {n === 1 && 'See a photo of yourself on screen'}
                                    {n === 2 && 'Point your camera at the QR code'}
                                    {n === 3 && "Claim your album — first one's free"}
                                 </Typography>
                              </Box>
                           ))}
                        </Grid>
                     </Grid>
                  </Paper>
               </Grid>
            </Grid>

            {/* Feed split */}
            <Grid container spacing={8} alignItems='center'>
               <Grid item xs={12} md={6}>
                  <Typography
                     sx={{
                        fontFamily: FONT_MONO,
                        fontSize: 9,
                        letterSpacing: 2.5,
                        textTransform: 'uppercase',
                        color: TEAL,
                        mb: 2,
                     }}
                  >
                     Your Personal Feed
                  </Typography>
                  <Typography
                     sx={{
                        fontFamily: FONT_SERIF,
                        fontSize: { xs: 32, md: 52 },
                        fontWeight: 300,
                        lineHeight: 1.1,
                        letterSpacing: -0.5,
                        color: 'white',
                        mb: 2,
                     }}
                  >
                     Every event.
                     <br />
                     <Box component='em' sx={{ color: TEAL }}>
                        One place.
                     </Box>
                  </Typography>
                  <Typography
                     sx={{ fontSize: 16, fontWeight: 300, color: MUTED, lineHeight: 1.8, mb: 3.5 }}
                  >
                     Your WeTrippen feed is the only place where all your event photos live together
                     — organized by date, photographer, and event. Not scattered across seven Google
                     Drive links.
                  </Typography>
                  <List disablePadding>
                     {[
                        "Photos from every WeTrippen event you've attended",
                        'Follow photographers who shoot you regularly',
                        'Save favorites, download originals',
                        'Share directly to Instagram, TikTok, anywhere',
                        'RSVP to upcoming events, get access early',
                        'Private albums from event organizers',
                     ].map((text, i) => (
                        <ListItem
                           key={i}
                           disableGutters
                           sx={{ alignItems: 'flex-start', gap: 1.5, py: 0.5 }}
                        >
                           <Box
                              sx={{
                                 width: 20,
                                 height: 20,
                                 borderRadius: '50%',
                                 flexShrink: 0,
                                 mt: 0.25,
                                 border: '1px solid rgba(0,196,167,0.25)',
                                 bgcolor: 'rgba(0,196,167,0.12)',
                                 display: 'flex',
                                 alignItems: 'center',
                                 justifyContent: 'center',
                                 color: TEAL,
                              }}
                           >
                              <CheckIcon sx={{ fontSize: 12 }} />
                           </Box>
                           <Typography
                              sx={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6 }}
                           >
                              {text}
                           </Typography>
                        </ListItem>
                     ))}
                  </List>
               </Grid>
               <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Paper
                     elevation={0}
                     sx={{
                        bgcolor: BG_S,
                        borderRadius: 4,
                        border: '1px solid rgba(255,255,255,0.08)',
                        overflow: 'hidden',
                        maxWidth: 280,
                        boxShadow: '0 32px 80px rgba(0,0,0,0.5)',
                     }}
                  >
                     <Box
                        sx={{
                           py: 1.75,
                           px: 2.25,
                           borderBottom: '1px solid rgba(255,255,255,0.06)',
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'space-between',
                        }}
                     >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                           <Box
                              component='img'
                              src={phoneAvatarPhoto}
                              alt='Jordan R.'
                              onError={(e) => {
                                 e.currentTarget.style.display = 'none';
                              }}
                              sx={{
                                 width: 30,
                                 height: 30,
                                 borderRadius: '50%',
                                 objectFit: 'cover',
                              }}
                           />
                           <Box>
                              <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'white' }}>
                                 Jordan R.
                              </Typography>
                              <Typography
                                 sx={{
                                    fontFamily: FONT_MONO,
                                    fontSize: 8,
                                    letterSpacing: 1,
                                    textTransform: 'uppercase',
                                    color: MUTED,
                                 }}
                              >
                                 12 events · 147 photos
                              </Typography>
                           </Box>
                        </Box>
                        <Typography
                           sx={{
                              fontFamily: FONT_SERIF,
                              fontSize: 16,
                              fontStyle: 'italic',
                              color: TEAL,
                           }}
                        >
                           W
                        </Typography>
                     </Box>
                     <Grid container sx={{ gap: 0 }}>
                        {phoneGridPhotos.map((src, i) => (
                           <Grid item xs={4} key={i} sx={{ aspectRatio: 1, p: 0.25 }}>
                              <Box
                                 component='img'
                                 src={src}
                                 alt='Feed photo'
                                 loading='lazy'
                                 onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                 }}
                                 sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block',
                                 }}
                              />
                           </Grid>
                        ))}
                        <Grid item xs={4} sx={{ aspectRatio: 1, p: 0.25 }}>
                           <Box
                              sx={{
                                 width: '100%',
                                 height: '100%',
                                 bgcolor: 'rgba(0,196,167,0.06)',
                                 border: '1px solid rgba(0,196,167,0.15)',
                                 display: 'flex',
                                 flexDirection: 'column',
                                 alignItems: 'center',
                                 justifyContent: 'center',
                                 gap: 0.25,
                              }}
                           >
                              <Typography
                                 sx={{
                                    fontFamily: FONT_MONO,
                                    fontSize: 7,
                                    letterSpacing: 1.5,
                                    textTransform: 'uppercase',
                                    color: TEAL,
                                 }}
                              >
                                 New
                              </Typography>
                              <Typography
                                 sx={{
                                    fontFamily: FONT_SERIF,
                                    fontSize: 20,
                                    fontWeight: 600,
                                    color: 'white',
                                 }}
                              >
                                 +23
                              </Typography>
                           </Box>
                        </Grid>
                     </Grid>
                     <Box
                        sx={{
                           py: 1.75,
                           px: 2.25,
                           borderTop: '1px solid rgba(255,255,255,0.06)',
                           display: 'flex',
                        }}
                     >
                        <Box sx={{ flex: 1, textAlign: 'center' }}>
                           <Typography
                              sx={{
                                 fontFamily: FONT_SERIF,
                                 fontSize: 20,
                                 fontWeight: 600,
                                 lineHeight: 1,
                                 color: 'white',
                              }}
                           >
                              12
                           </Typography>
                           <Typography
                              sx={{
                                 fontFamily: FONT_MONO,
                                 fontSize: 7,
                                 letterSpacing: 1.5,
                                 textTransform: 'uppercase',
                                 color: MUTED,
                                 mt: 0.25,
                              }}
                           >
                              Events
                           </Typography>
                        </Box>
                        <Box
                           sx={{
                              flex: 1,
                              textAlign: 'center',
                              borderLeft: '1px solid rgba(255,255,255,0.06)',
                           }}
                        >
                           <Typography
                              sx={{
                                 fontFamily: FONT_SERIF,
                                 fontSize: 20,
                                 fontWeight: 600,
                                 lineHeight: 1,
                                 color: 'white',
                              }}
                           >
                              147
                           </Typography>
                           <Typography
                              sx={{
                                 fontFamily: FONT_MONO,
                                 fontSize: 7,
                                 letterSpacing: 1.5,
                                 textTransform: 'uppercase',
                                 color: MUTED,
                                 mt: 0.25,
                              }}
                           >
                              Photos
                           </Typography>
                        </Box>
                        <Box
                           sx={{
                              flex: 1,
                              textAlign: 'center',
                              borderLeft: '1px solid rgba(255,255,255,0.06)',
                           }}
                        >
                           <Typography
                              sx={{
                                 fontFamily: FONT_SERIF,
                                 fontSize: 20,
                                 fontWeight: 600,
                                 lineHeight: 1,
                                 color: 'white',
                              }}
                           >
                              8
                           </Typography>
                           <Typography
                              sx={{
                                 fontFamily: FONT_MONO,
                                 fontSize: 7,
                                 letterSpacing: 1.5,
                                 textTransform: 'uppercase',
                                 color: MUTED,
                                 mt: 0.25,
                              }}
                           >
                              Following
                           </Typography>
                        </Box>
                     </Box>
                  </Paper>
               </Grid>
            </Grid>
         </Box>

         {/* Studio callout */}
         <Box sx={{ maxWidth: 1080, mx: 'auto', px: 4, pb: 12 }}>
            <Paper
               elevation={0}
               component='a'
               href='https://studio.wetrippen.com'
               target='_blank'
               rel='noopener noreferrer'
               sx={{
                  display: 'block',
                  textDecoration: 'none',
                  color: 'inherit',
                  position: 'relative',
                  overflow: 'hidden',
                  bgcolor: BG_S,
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: 2,
                  '&:hover .studio-cta-arrow': { transform: 'translateX(4px)' },
               }}
            >
               <Box
                  sx={{
                     position: 'absolute',
                     top: 0,
                     right: 0,
                     width: 180,
                     height: 180,
                     background:
                        'radial-gradient(circle at 100% 0%, rgba(240,165,0,0.12) 0%, transparent 70%)',
                     pointerEvents: 'none',
                  }}
               />
               <Box sx={{ position: 'relative', p: 5 }}>
                  <Box
                     sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 1,
                        bgcolor: 'rgba(240,165,0,0.08)',
                        border: '1px solid rgba(240,165,0,0.2)',
                     }}
                  >
                     <Box
                        sx={{
                           width: 6,
                           height: 6,
                           borderRadius: '50%',
                           bgcolor: AMBER,
                        }}
                     />
                     <Typography
                        sx={{
                           fontFamily: FONT_MONO,
                           fontSize: 10,
                           letterSpacing: 1.8,
                           textTransform: 'uppercase',
                           color: AMBER,
                           fontWeight: 500,
                        }}
                     >
                        Are you a photographer?
                     </Typography>
                  </Box>
                  <Typography
                     sx={{
                        fontFamily: FONT_SERIF,
                        fontSize: { xs: 24, md: 32 },
                        fontWeight: 400,
                        color: 'white',
                        lineHeight: 1.2,
                        letterSpacing: -0.5,
                        mb: 1.25,
                     }}
                  >
                     Your photos reach every person you captured —
                  </Typography>
                  <Typography
                     component='span'
                     sx={{
                        fontFamily: FONT_SERIF,
                        fontSize: { xs: 24, md: 32 },
                        fontWeight: 700,
                        color: AMBER,
                        lineHeight: 1.2,
                        letterSpacing: -0.5,
                        display: 'block',
                        mb: 2,
                     }}
                  >
                     automatically.
                  </Typography>
                  <Typography
                     sx={{
                        fontSize: 13,
                        color: MUTED,
                        lineHeight: 1.65,
                        maxWidth: 420,
                        mb: 3,
                     }}
                  >
                     Sequel Studio handles camera tethering, live display, QR delivery, and
                     pay-per-photo. Your clients find their photos on WeTrippen — no emails, no
                     Google Drive links, no follow-up.
                  </Typography>
                  <Box
                     className='studio-cta-arrow'
                     sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        fontFamily: FONT_SANS,
                        fontSize: 14,
                        fontWeight: 700,
                        color: AMBER,
                        transition: 'transform 0.2s ease',
                     }}
                  >
                     Explore Sequel Studio
                     <Typography component='span' sx={{ fontSize: 18, lineHeight: 1 }}>
                        →
                     </Typography>
                  </Box>
               </Box>
            </Paper>
         </Box>

         {/* CTA block */}
         <Box sx={{ maxWidth: 1080, mx: 'auto', px: 4, pb: 12 }}>
            <Paper
               elevation={0}
               sx={{
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: BG_S,
                  position: 'relative',
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'stretch', md: 'center' },
                  minHeight: { xs: 'auto', md: 200 },
               }}
            >
               {/* Teal accent stripe */}
               <Box
                  sx={{
                     position: 'absolute',
                     left: 0,
                     top: 0,
                     bottom: 0,
                     width: 4,
                     bgcolor: TEAL,
                  }}
               />
               <Box
                  sx={{
                     flex: 1,
                     py: 5,
                     px: 4,
                     pl: { xs: 4, md: 5 },
                     display: 'flex',
                     flexDirection: 'column',
                     justifyContent: 'center',
                  }}
               >
                  <Typography
                     sx={{
                        fontFamily: FONT_SERIF,
                        fontSize: { xs: 22, md: 28 },
                        fontWeight: 400,
                        letterSpacing: -0.5,
                        color: 'white',
                        lineHeight: 1.25,
                        mb: 0.75,
                     }}
                     data-testid='final-cta-title'
                  >
                     Your photos are out there.
                  </Typography>
                  <Typography
                     component='span'
                     sx={{
                        fontFamily: FONT_SERIF,
                        fontSize: { xs: 22, md: 28 },
                        fontWeight: 400,
                        letterSpacing: -0.5,
                        color: TEAL,
                        lineHeight: 1.25,
                        display: 'block',
                        mb: 1.5,
                     }}
                  >
                     Go find them.
                  </Typography>
                  <Typography
                     sx={{
                        fontSize: 13,
                        fontWeight: 400,
                        color: MUTED,
                        lineHeight: 1.6,
                        maxWidth: 400,
                     }}
                     data-testid='final-cta-subtitle'
                  >
                     Your first event is on us. After that, $35/year unlocks unlimited events, albums,
                     and photos across every photographer you follow.
                  </Typography>
               </Box>
               <Box
                  sx={{
                     py: 5,
                     px: 4,
                     pr: 5,
                     display: 'flex',
                     flexDirection: 'column',
                     alignItems: { xs: 'stretch', md: 'flex-end' },
                     justifyContent: 'center',
                     gap: 2,
                     borderLeft: { xs: 'none', md: '1px solid rgba(255,255,255,0.06)' },
                     borderTop: { xs: '1px solid rgba(255,255,255,0.06)', md: 'none' },
                  }}
               >
                  <Button
                     onClick={() => navigate('/signup')}
                     sx={{
                        fontFamily: FONT_SANS,
                        fontSize: 14,
                        fontWeight: 700,
                        bgcolor: TEAL,
                        color: '#000',
                        borderRadius: 1.5,
                        py: 1.75,
                        px: 4,
                        minWidth: { xs: 'auto', md: 200 },
                        boxShadow: '0 2px 16px rgba(0,196,167,0.25)',
                        '&:hover': {
                           bgcolor: '#00dfc0',
                           boxShadow: '0 4px 24px rgba(0,196,167,0.35)',
                        },
                     }}
                     data-testid='final-cta-button'
                  >
                     Create Free Account
                  </Button>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: { xs: 'flex-start', md: 'flex-end' } }}>
                     <Typography sx={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)' }}>
                        ✓ First event free
                     </Typography>
                     <Typography sx={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)' }}>
                        ✓ $35/year unlimited after
                     </Typography>
                     <Typography sx={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)' }}>
                        ✓ No credit card to start
                     </Typography>
                  </Box>
               </Box>
            </Paper>
         </Box>

         {/* Footer */}
         <Box
            component='footer'
            sx={{
               borderTop: '1px solid rgba(255,255,255,0.05)',
               py: 5,
               px: 4,
               maxWidth: 1080,
               mx: 'auto',
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'space-between',
               flexWrap: 'wrap',
               gap: 2.5,
            }}
            data-testid='footer'
         >
            <Typography
               sx={{
                  fontFamily: FONT_SERIF,
                  fontSize: 20,
                  fontWeight: 600,
                  fontStyle: 'italic',
                  color: 'white',
               }}
            >
               WeTrippen
            </Typography>
            <Box sx={{ display: 'flex', gap: 3 }}>
               <Button
                  onClick={() => navigate('/privacy')}
                  sx={{
                     fontFamily: FONT_MONO,
                     fontSize: 9,
                     letterSpacing: 1.5,
                     textTransform: 'uppercase',
                     color: 'white',
                     minWidth: 0,
                     '&:hover': { color: 'rgba(255,255,255,0.85)' },
                  }}
               >
                  Privacy
               </Button>
               <Button
                  onClick={() => navigate('/terms')}
                  sx={{
                     fontFamily: FONT_MONO,
                     fontSize: 9,
                     letterSpacing: 1.5,
                     textTransform: 'uppercase',
                     color: 'white',
                     minWidth: 0,
                     '&:hover': { color: 'rgba(255,255,255,0.85)' },
                  }}
               >
                  Terms
               </Button>
               <Button
                  onClick={() => navigate('/contact')}
                  sx={{
                     fontFamily: FONT_MONO,
                     fontSize: 9,
                     letterSpacing: 1.5,
                     textTransform: 'uppercase',
                     color: 'white',
                     minWidth: 0,
                     '&:hover': { color: 'rgba(255,255,255,0.85)' },
                  }}
               >
                  Support
               </Button>
               <Link
                  href='https://studio.wetrippen.com'
                  target='_blank'
                  rel='noopener noreferrer'
                  sx={{
                     fontFamily: FONT_MONO,
                     fontSize: 9,
                     letterSpacing: 1.5,
                     textTransform: 'uppercase',
                     color: 'white',
                     textDecoration: 'none',
                     '&:hover': { color: 'rgba(255,255,255,0.85)' },
                  }}
               >
                  Sequel Studio — For Photographers →
               </Link>
            </Box>
            <Typography
               sx={{
                  fontFamily: FONT_MONO,
                  fontSize: 9,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                  color: 'white',
               }}
            >
               © 2026 WeTrippen Inc.
            </Typography>
         </Box>

         <style>{`@keyframes landingStripScroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
         @keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.4;transform:scale(0.8)} }`}</style>
      </Box>
   );
}
